//#region Modules
import type {
  IUserDetailsRecord,
  DeepReadonlyT,
  IUserWhatsAppPersonalRecord,
  IUserWhatsAppWorkspaceRecord,
  IUserSettingsRecord,
  IUserDetailsMyDayConfig,
  IUserSuggestionFilter,
  ISpaceRecord,
  IUserDetailsSyncRecord,
} from "@anydo/interfaces";
import { immerable } from "@anydo/vendor/immer";
import { NOT_INITIALIZED_ID } from "..";
import { areNullableObjectsShallowEqual } from "../utils/lang/areNullableObjectsShallowEqual";
import { IRecord } from "./-private/IRecord";
import { RecordUtils } from "./-private/RecordUtils";
import { UserSettingsRecord } from "./UserSettingsRecord";
//#endregion

//#region Default properties
const getDefaultProperty = RecordUtils.createGetDefaultProperty<IUserDetailsRecord>({
  creationDate: null,
  email: "",
  googleAvatarUrl: null,
  id: "",
  idSalt: "",
  name: "",
  profilePicture: null,
  onBoardingPending: false,
  timezone: null,
  verifiedPhoneNumber: null,
  whatsapp: null,
  workspaceWhatsapp: null,
  completedCounter: null,
  firstDayOfWeek: null,
  webConfig: null,
  myDayConfig: null,
  myDaySuggestionFilters: null,
  isPremium: false,
  isReverseTrial: false,
  hasUnreadNotifications: false,
  dirty: 0,
  lastUpdateDate: null,
  activeTimeTracker: null,
});
//#endregion

//#region Helpers
function getSerializedValue<K extends keyof IUserDetailsRecord>(
  key: K,
  value: IUserDetailsRecord[K],
): IUserDetailsRecord[K] {
  type V = IUserDetailsRecord[K];

  switch (key) {
    case "whatsapp":
      return getSerializeWhatsappValue(value as IUserDetailsRecord["whatsapp"]) as V;

    case "workspaceWhatsapp":
      return getSerializeWhatsappWorkspaceValue(value as IUserDetailsRecord["workspaceWhatsapp"]) as V;

    case "webConfig":
      return getSerializeWebConfigValue(value as IUserDetailsRecord["webConfig"]) as V;

    default:
      return value as IUserDetailsRecord[K];
  }
}

function getSerializeWebConfigValue(value: IUserDetailsRecord["webConfig"]): IUserDetailsRecord["webConfig"] {
  return value == null ? null : UserSettingsRecord.create(value);
}

function getSerializeWhatsappValue(value: IUserDetailsRecord["whatsapp"]): IUserDetailsRecord["whatsapp"] {
  return value === null ? null : (
      {
        isReminders: value.isReminders,
      }
    );
}

function getSerializeWhatsappWorkspaceValue(
  value: IUserDetailsRecord["workspaceWhatsapp"],
): IUserDetailsRecord["workspaceWhatsapp"] {
  return value === null ? null : (
      {
        isReminders: value.isReminders,
        isUserUpdates: value.isUserUpdates,
        isBoardUpdates: value.isBoardUpdates,
      }
    );
}

//#endregion

export const UserDetailsRecord = {
  create(props?: Partial<IUserDetailsRecord>): IUserDetailsRecord {
    return {
      id: getDefaultProperty("id", props),
      creationDate: getDefaultProperty("creationDate", props),
      email: getDefaultProperty("email", props),
      googleAvatarUrl: getDefaultProperty("googleAvatarUrl", props),
      idSalt: getDefaultProperty("idSalt", props),
      name: getDefaultProperty("name", props),
      profilePicture: getSerializedValue("profilePicture", getDefaultProperty("profilePicture", props)),
      onBoardingPending: getDefaultProperty("onBoardingPending", props),
      timezone: getSerializedValue("timezone", getDefaultProperty("timezone", props)),
      verifiedPhoneNumber: getSerializedValue("verifiedPhoneNumber", getDefaultProperty("verifiedPhoneNumber", props)),
      whatsapp: getSerializedValue("whatsapp", getDefaultProperty("whatsapp", props)),
      workspaceWhatsapp: getSerializedValue("workspaceWhatsapp", getDefaultProperty("workspaceWhatsapp", props)),
      completedCounter: getSerializedValue("completedCounter", getDefaultProperty("completedCounter", props)),
      firstDayOfWeek: getSerializedValue("firstDayOfWeek", getDefaultProperty("firstDayOfWeek", props)),
      webConfig: getSerializedValue("webConfig", getDefaultProperty("webConfig", props)),
      myDayConfig: getDefaultProperty("myDayConfig", props),
      myDaySuggestionFilters: getDefaultProperty("myDaySuggestionFilters", props),
      isPremium: getDefaultProperty("isPremium", props),
      isReverseTrial: getDefaultProperty("isReverseTrial", props),
      hasUnreadNotifications: getDefaultProperty("hasUnreadNotifications", props),
      dirty: getDefaultProperty("dirty", props),
      lastUpdateDate: getDefaultProperty("lastUpdateDate", props),
      activeTimeTracker: getDefaultProperty("activeTimeTracker", props),
    };
  },

  set<K extends keyof IUserDetailsRecord>(record: IUserDetailsRecord, key: K, value: IUserDetailsRecord[K]): void {
    if (!UserDetailsRecord.isKeyEqual(record, key, value)) {
      record[key] = getSerializedValue(key, value);
    }
  },

  isKeyEqual<K extends keyof IUserDetailsRecord>(
    record: IUserDetailsRecord,
    key: K,
    value: IUserDetailsRecord[K],
  ): boolean {
    if (RecordUtils.isEqual(record[key], value)) return true;

    if (key === "whatsapp" || key === "workspaceWhatsapp" || key === "webConfig") {
      // TS has hard time to infer it right
      return areNullableObjectsShallowEqual<any>(record[key], value);
    }

    return false;
  },

  merge(record: IUserDetailsRecord, from: DeepReadonlyT<IUserDetailsRecord>): void {
    record.id = from.id;
    record.creationDate = from.creationDate;
    record.email = from.email;
    record.googleAvatarUrl = from.googleAvatarUrl;
    record.idSalt = from.idSalt;
    record.name = from.name;
    record.profilePicture = from.profilePicture;
    record.onBoardingPending = from.onBoardingPending;
    record.timezone = from.timezone;
    record.verifiedPhoneNumber = from.verifiedPhoneNumber;
    record.completedCounter = from.completedCounter;
    record.firstDayOfWeek = from.firstDayOfWeek;
    record.myDayConfig = from.myDayConfig;
    record.myDaySuggestionFilters = from.myDaySuggestionFilters;
    record.isPremium = from.isPremium;
    record.hasUnreadNotifications = from.hasUnreadNotifications;
    record.lastUpdateDate = from.lastUpdateDate;

    //#region Objects

    if (!this.isKeyEqual(record, "whatsapp", from.whatsapp)) {
      record.whatsapp = getSerializeWhatsappValue(from.whatsapp);
    }

    if (!this.isKeyEqual(record, "workspaceWhatsapp", from.workspaceWhatsapp)) {
      record.workspaceWhatsapp = getSerializeWhatsappWorkspaceValue(from.workspaceWhatsapp);
    }

    if (!this.isKeyEqual(record, "webConfig", from.webConfig)) {
      record.webConfig = getSerializeWebConfigValue(from.webConfig);
    }
    //#endregion
  },
};

export class UserDetailsSyncRecord implements IUserDetailsRecord, IRecord<IUserDetailsSyncRecord> {
  //#region Static
  static [immerable] = true;

  static create(props?: IUserDetailsRecord) {
    return new UserDetailsSyncRecord(props);
  }

  static setImmutable<K extends keyof IUserDetailsRecord>(
    record: UserDetailsSyncRecord,
    key: K,
    value: IUserDetailsRecord[K],
  ): UserDetailsSyncRecord {
    return RecordUtils.updateImmutableInstace<IUserDetailsRecord, UserDetailsSyncRecord, K>(
      UserDetailsSyncRecord,
      record,
      key,
      value,
    );
  }

  static setMutable<K extends keyof IUserDetailsRecord>(
    record: IUserDetailsRecord,
    key: K,
    value: IUserDetailsRecord[K],
  ): void {
    (record[key] as IUserDetailsRecord[K]) = value;
  }

  static isKeyEqual<K extends keyof IUserDetailsRecord>(
    record: IUserDetailsRecord,
    key: K,
    value: IUserDetailsRecord[K],
  ): boolean {
    return RecordUtils.isEqual(record[key], value);
  }
  //#endregion

  creationDate: number | null;
  email: string;
  googleAvatarUrl: string | null;
  id: string;
  idSalt: string;
  name: string;
  profilePicture: string | null;
  onBoardingPending: boolean;
  timezone: string | null;
  verifiedPhoneNumber: string | null;
  /**
   * It's not `whatsApp` to align with server response
   */
  whatsapp: IUserWhatsAppPersonalRecord | null;
  workspaceWhatsapp: IUserWhatsAppWorkspaceRecord | null;
  completedCounter: number | null;
  firstDayOfWeek: number | null;
  webConfig: IUserSettingsRecord | null;
  myDayConfig: IUserDetailsMyDayConfig | null;
  myDaySuggestionFilters: IUserSuggestionFilter | null;
  currentUserSpaces: ISpaceRecord["id"][];

  isPremium: boolean;
  isReverseTrial: boolean;

  activeTimeTracker: { cardId: string; startTime: number } | null;

  lastUpdateDate: number | null;
  hasUnreadNotifications: boolean;
  dirty: 0 | 1;

  constructor(props?: Partial<IUserDetailsSyncRecord>) {
    this.id = getDefaultProperty("id", props);
    this.creationDate = getDefaultProperty("creationDate", props);
    this.email = getDefaultProperty("email", props);
    this.googleAvatarUrl = getDefaultProperty("googleAvatarUrl", props);
    this.idSalt = getDefaultProperty("idSalt", props);
    this.name = getDefaultProperty("name", props);
    this.profilePicture = getDefaultProperty("profilePicture", props);
    this.onBoardingPending = getDefaultProperty("onBoardingPending", props);
    this.timezone = getDefaultProperty("timezone", props);
    this.verifiedPhoneNumber = getDefaultProperty("verifiedPhoneNumber", props);
    this.whatsapp = getDefaultProperty("whatsapp", props);
    this.workspaceWhatsapp = getDefaultProperty("workspaceWhatsapp", props);
    this.completedCounter = getDefaultProperty("completedCounter", props);
    this.firstDayOfWeek = getDefaultProperty("firstDayOfWeek", props);
    this.webConfig = getDefaultProperty("webConfig", props);
    this.myDayConfig = getDefaultProperty("myDayConfig", props);
    this.myDaySuggestionFilters = getDefaultProperty("myDaySuggestionFilters", props);
    this.isPremium = getDefaultProperty("isPremium", props);
    this.isReverseTrial = getDefaultProperty("isReverseTrial", props);
    this.hasUnreadNotifications = getDefaultProperty("hasUnreadNotifications", props);
    this.dirty = getDefaultProperty("dirty", props);
    this.lastUpdateDate = getDefaultProperty("lastUpdateDate", props);
    this.currentUserSpaces = props?.currentUserSpaces || [];
    this.activeTimeTracker = getDefaultProperty("activeTimeTracker", props);

    //#region Validation
    if (this.id === NOT_INITIALIZED_ID) throw new Error("UserDetailsSyncRecord: `id` wasn't initialized");
    //#endregion
  }

  //#region Methods
  get<K extends keyof IUserDetailsSyncRecord>(key: K): UserDetailsSyncRecord[K] {
    return this[key];
  }

  set<K extends keyof IUserDetailsSyncRecord>(key: K, value: IUserDetailsSyncRecord[K]): UserDetailsSyncRecord {
    return RecordUtils.updateImmutableInstace<IUserDetailsSyncRecord, UserDetailsSyncRecord, K>(
      UserDetailsSyncRecord,
      this,
      key,
      value,
    );
  }
  //#endregion
}

export function isUserDetailsSyncRecord(obj: any): obj is UserDetailsSyncRecord {
  return obj != null && obj instanceof UserDetailsSyncRecord;
}
